import { omit } from "lodash";
import { getFromDataPath, mutateByPath } from "../../../utility";
import { isWhereClauseWithCompoundExpression, isWhereClauseWithCondition } from "../../typeGuards";
import { normalizeSubClauseLevelsAndOrders } from "../../utility/parseWhereClauseFromCode";
import { ArsContextState, IArsActionRemoveWhereClause } from "../interface";
import { addPatchToStack } from "./addPatchToStack";

/**
 * Removes a specific sub-clause from a where clause path.
 *
 * @param state - Current ARS context state
 * @param action - Remove-where-clause action
 * @returns Updated state with patch metadata when data changed
 */
export const arsc_removeWhereClause = <T>(
  state: ArsContextState<T>,
  action: IArsActionRemoveWhereClause,
): ArsContextState<T> => {
  const whereClause = getFromDataPath(action.dataPath, state, isWhereClauseWithCompoundExpression);
  let removedState = state;

  if (whereClause) {
    const compoundExpression = whereClause.compoundExpression;
    if (!compoundExpression.whereClauses || compoundExpression.whereClauses.length <= 1) {
      throw new Error(
        `Cannot remove where clause from ${action.dataPath} because it does not have multiple clauses`,
      );
    } else if (compoundExpression.whereClauses.length === 2) {
      const remainingClause = compoundExpression.whereClauses.find(
        (clause) => clause.id !== action.id,
      );
      if (remainingClause) {
        if (isWhereClauseWithCondition(remainingClause)) {
          const newEntity = {
            ...omit(whereClause, "compoundExpression"),
            condition: remainingClause.condition,
            level: remainingClause.level - 1,
            order: 0,
          };
          removedState = mutateByPath({ ...state, changed: true }, action.dataPath, newEntity);
        } else if (isWhereClauseWithCompoundExpression(remainingClause)) {
          const parentPath = action.dataPath.substring(
            0,
            action.dataPath.lastIndexOf("/compoundExpression/whereClauses"),
          );
          const parentEntity = getFromDataPath(
            parentPath,
            state,
            isWhereClauseWithCompoundExpression,
          );
          const normalizedCompound = {
            ...remainingClause.compoundExpression,
            whereClauses: remainingClause.compoundExpression.whereClauses?.map((clause) => ({
              ...clause,
            })),
          };

          if (parentEntity && parentEntity.compoundExpression.whereClauses?.length === 1) {
            normalizedCompound.whereClauses?.forEach((clause, index) => {
              normalizeSubClauseLevelsAndOrders(clause, parentEntity.level + 1, index);
            });
            removedState = mutateByPath(
              { ...state, changed: true },
              `${parentPath}/compoundExpression`,
              normalizedCompound,
            );
          } else {
            normalizedCompound.whereClauses?.forEach((clause, index) => {
              normalizeSubClauseLevelsAndOrders(clause, whereClause.level + 1, index);
            });
            const newEntity = {
              ...whereClause,
              compoundExpression: normalizedCompound,
              level: whereClause.level,
              order: whereClause.order,
            };
            removedState = mutateByPath({ ...state, changed: true }, action.dataPath, newEntity);
          }
        } else if ("subClauseId" in whereClause) {
          const newEntity = {
            ...omit(whereClause, "compoundExpression"),
            subClauseId: remainingClause.subClauseId,
            level: remainingClause.level - 1,
            order: whereClause.order,
          };
          removedState = mutateByPath({ ...state, changed: true }, action.dataPath, newEntity);
        } else {
          throw new Error(
            `Cannot collapse to single clause at ${action.dataPath} - remaining clause is a reference`,
          );
        }
      }
    } else {
      const newWhereClauses = compoundExpression.whereClauses.filter(
        (clause) => clause.id !== action.id,
      );
      const newCompoundExpression = {
        ...compoundExpression,
        whereClauses: newWhereClauses,
      };
      removedState = mutateByPath(
        { ...state, changed: true },
        `${action.dataPath}/compoundExpression`,
        newCompoundExpression,
      );
    }
  }

  if (removedState !== state) {
    return addPatchToStack(state, removedState);
  }

  return state;
};
